import { logger } from '@/core/logger';
import { Image, ImageContentFit } from 'expo-image';
import React from 'react';
import { ImageStyle, StyleProp, View, ViewStyle } from 'react-native';
import { PlaceholderImage } from './PlaceholderImage';

export interface NetworkImageProps {
    url?: string | null;
    style?: StyleProp<ViewStyle>;
    resizeMode?: 'cover' | 'contain' | 'stretch' | 'center' | 'repeat';
    transition?: number;
    /** Called once when the image fails to load (the placeholder is shown either way). */
    onError?: () => void;
}

// Map React Native resizeMode to expo-image contentFit
const mapResizeMode = (resizeMode: NetworkImageProps['resizeMode']): ImageContentFit => {
    switch (resizeMode) {
        case 'cover':
            return 'cover';
        case 'contain':
            return 'contain';
        case 'stretch':
            return 'fill';
        case 'center':
            return 'scale-down';
        case 'repeat':
            return 'contain'; // expo-image doesn't support repeat, fallback to contain
        default:
            return 'cover';
    }
};

export const NetworkImage = React.memo<NetworkImageProps>(function NetworkImage({ url, style, resizeMode = 'cover', transition = 200, onError }) {
    const [failed, setFailed] = React.useState(false);

    // Reset failed state when URL changes
    React.useEffect(() => {
        setFailed(false);
    }, [url]);

    if (!url || failed) {
        return (
            <View style={style}>
                <PlaceholderImage />
            </View>
        );
    }

    // Ensure URL is valid and properly formatted
    const imageUrl = url.trim();
    if (!imageUrl || (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://'))) {
        logger.warn('NetworkImage', 'Invalid image URL', { url: imageUrl });
        return (
            <View style={style}>
                <PlaceholderImage />
            </View>
        );
    }

    return (
        <Image
            source={{ uri: imageUrl }}
            style={style as StyleProp<ImageStyle>}
            contentFit={mapResizeMode(resizeMode)}
            onError={(error) => {
                logger.error('NetworkImage', 'Failed to load image', { url: imageUrl, error });
                setFailed(true);
                onError?.();
            }}
            onLoadStart={() => {
                logger.debug('NetworkImage', 'Loading image', { url: imageUrl });
            }}
            onLoad={() => {
                logger.debug('NetworkImage', 'Image loaded successfully', { url: imageUrl });
            }}
            cachePolicy="memory-disk"
            transition={transition}
            recyclingKey={imageUrl}
            priority={transition === 0 ? 'high' : 'normal'}
        />
    );
});

export default NetworkImage;


